import {
  addCollagePhotos,
  collageSpares,
  collageStatus,
  emptyCollageAnswer,
  exchangeCollage,
  planCollagePlacement,
  randomCollageSeed,
  replaceInSlot,
  type CollageAnswer,
  type CollageContext,
  type CollagePhoto,
  type CollageRules,
  type CollageSlotGeom,
} from '@pz/scene';
import { UploadError, type CollageUploadResult } from '../api.js';
import {
  dropPending,
  fitForUpload,
  headerSize,
  keepPending,
  type PendingPicture,
  type PictureSize,
} from '../photo-intake.js';
import { COLLAGE_TEXTS } from './texts.js';

/**
 * Taking a collage's many photos in (docs/photo-collage.md §7, "Uploads").
 *
 * One pick of 24 photos becomes 24 uploads that fill the product while the
 * shopper watches. What this owns:
 *
 *  - admission: duplicates of photos already in (same name, size and date)
 *    are skipped, and no more than the product holds (active slots + spares)
 *    is taken — the rest refused out loud;
 *  - a provisional arrangement from the files' HEADERS (nothing is decoded
 *    here), so the uploads go biggest planned slot first and the preview
 *    fills where it matters most;
 *  - each file kept in IndexedDB (by its own index) before it is sent, so a
 *    reload finishes the batch by itself;
 *  - sending 2 at a time (1 on a slow or data-saving connection), a busy
 *    server waited out with jittered pauses and one upload fewer at a time,
 *    a lost connection retried after 2, 4 and 8 s and paused while offline,
 *    an answer about the file itself (413/415) shown and never retried;
 *  - every landed photo written into the answer through the pure `@pz/scene`
 *    commands: its planned slot when that is still empty, otherwise wherever
 *    the placement puts it — a slot the shopper filled meanwhile is theirs;
 *  - once a batch is in and the shopper touched nothing meanwhile, one more
 *    pass with the same seed and the detected faces, kept only when it cuts
 *    fewer faces.
 *
 * No cut-out, style or any paid call is ever made for a collage photo.
 */

export type CollageJobState = 'waiting' | 'sending' | 'retrying' | 'done' | 'failed';

/** One picked file on its way. */
export interface CollageJob {
  /** The IndexedDB index it is kept under: "<batch>.<n>". */
  key: string;
  batch: string;
  file: File;
  name: string;
  /** `name|size|lastModified` — the same photo picked twice is skipped. */
  fingerprint: string;
  bytes: number;
  /** From the header; undefined for a format the reader does not know. */
  size?: PictureSize;
  /** Where the provisional plan put it; undefined = a spare. */
  plannedSlot?: string;
  plannedArea: number;
  /** "Nova slika" / "Dodaj sliku ovde": this one photo replaces that slot's. */
  target?: string;
  /** Its place in pick order (the answer's pool keeps it, whatever order the uploads land in). */
  order: number;
  state: CollageJobState;
  /** A failed job "Pošalji ponovo" can send again (false for 413/415). */
  retryable: boolean;
  message?: string;
  assetId?: string;
  resumed: boolean;
  busyTries: number;
  networkTries: number;
  /** Resolves once the file is kept (or could not be). */
  kept: Promise<void>;
}

/** Where a landed photo's pixels are. */
export interface CollageMedia {
  assetId: string;
  /** Long side ≤ 1024: what the preview renders. */
  drawUrl: string;
  /** Long side ≤ 256: the tray. */
  thumbUrl: string;
  /** The 2400 px preview copy: the framer only. */
  framerUrl: string;
  /** Upright size of the original. */
  size?: { w: number; h: number };
  fingerprint: string;
}

/** What the intake needs from the question it fills (host.ts gives it). */
export interface CollageIntakeHost {
  readonly rules: CollageRules;
  /** Active slots, now (after every current answer resolves). */
  activeSlots(): CollageSlotGeom[];
  answer(): CollageAnswer | null;
  /**
   * A photo landed: the answer becomes `apply(answer)`, and every undo
   * snapshot gets the same (an upload is not something "Poništi" takes back).
   */
  land(apply: (answer: CollageAnswer | null) => CollageAnswer | null): void;
  /** The final pass's re-arrangement: stored only if the answer is still `from`. */
  settle(from: CollageAnswer, next: CollageAnswer): void;
  /** A shopper action (a replaced slot): undoable. */
  commit(next: CollageAnswer): void;
  /** How many shopper changes so far; the final pass needs none since its batch began. */
  touches(): number;
  /** A photo's URLs landed: register the draw copy with the preview, keep the rest. */
  media(media: CollageMedia): void;
}

export interface CollageIntakeEnv {
  /** From the file's header only — never a decode. */
  pictureSize(file: File): Promise<PictureSize | undefined>;
  fitForUpload(file: File, size: PictureSize | undefined): Promise<File>;
  keepPending(scope: string, optionId: string, file: File, index: string): Promise<void>;
  dropPending(scope: string, optionId: string, index: string): Promise<void>;
  sleep(ms: number): Promise<void>;
  random(): number;
  now(): number;
  online(): boolean;
  /** Resolves when the connection is back (or the page is shown again). */
  whenOnline(): Promise<void>;
  /** Save-data, 2g or 3g: one upload at a time instead of two. */
  slow(): boolean;
}

export interface CollageIntakeOptions {
  optionId: string;
  /** The pending-picture scope (`productId:templateId`). */
  scope: string;
  upload(file: File, name: string): Promise<CollageUploadResult>;
  host: CollageIntakeHost;
  flushDraft?(): void;
  /** Something the step shows changed. */
  onUpdate?(): void;
  env?: Partial<CollageIntakeEnv>;
}

export interface CollageAddOutcome {
  accepted: number;
  duplicates: number;
  refused: number;
  /** The capacity sentence, when some or all were refused. */
  message?: string;
}

export interface CollageProgress {
  done: number;
  total: number;
  bytesDone: number;
  bytesTotal: number;
}

interface Batch {
  id: string;
  seed: number;
  touchesAtStart: number;
  jobs: CollageJob[];
  resumed: boolean;
  settled: boolean;
}

/** Busy retries per photo, and the pause range before each (ms). */
export const COLLAGE_BUSY_TRIES = 6;
const BUSY_PAUSE = [2000, 5000] as const;
/** Pauses before the network retries of one photo. */
export const COLLAGE_NETWORK_PAUSES = [2000, 4000, 8000] as const;
/** A busy server is mentioned once it has been busy this long. */
const BUSY_NOTICE_MS = 5000;

/** `name|size|lastModified`: how a picked file is recognised when it comes again. */
export function collageFingerprint(file: Pick<File, 'name' | 'size'> & { lastModified?: number }): string {
  return `${file.name}|${file.size}|${file.lastModified ?? 0}`;
}

/**
 * The same photo? A file back from IndexedDB may have lost its date (a
 * browser that stored its bytes), so a resumed one matches on name and size.
 */
function sameSource(a: string, b: string, loose: boolean): boolean {
  if (a === b) return true;
  if (!loose) return false;
  const cut = (f: string): string => f.slice(0, f.lastIndexOf('|'));
  return cut(a) === cut(b);
}

function defaultEnv(): CollageIntakeEnv {
  return {
    // The header only: a collage takes dozens of files at once and never
    // decodes one in the page (an unknown format simply stays unmeasured).
    pictureSize: (file) => headerSize(file),
    fitForUpload: (file, size) => fitForUpload(file, size),
    keepPending: (scope, optionId, file, index) => keepPending(scope, optionId, file, index),
    dropPending: (scope, optionId, index) => dropPending(scope, optionId, index),
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    random: () => Math.random(),
    now: () => Date.now(),
    online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
    whenOnline: () =>
      new Promise<void>((resolve) => {
        if (typeof window === 'undefined') return resolve();
        const done = (): void => {
          window.removeEventListener('online', done);
          document.removeEventListener?.('visibilitychange', shown);
          resolve();
        };
        const shown = (): void => {
          if (document.visibilityState === 'visible') done();
        };
        window.addEventListener('online', done);
        document.addEventListener?.('visibilitychange', shown);
      }),
    slow: () => {
      const connection = (typeof navigator !== 'undefined'
        ? (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection
        : undefined);
      return connection?.saveData === true || /(^|-)(2g|3g)$/.test(connection?.effectiveType ?? '');
    },
  };
}

/** How an upload failed: wait for the server, wait for the network, or give up on this file. */
export function classifyUploadError(err: unknown): 'busy' | 'network' | 'final' | 'other' {
  if (err instanceof UploadError) return err.code === 'conversion_busy' ? 'busy' : err.kind;
  const e = err as { name?: string; kind?: unknown; code?: unknown; status?: unknown } | undefined;
  if (e && (e.kind === 'busy' || e.kind === 'network' || e.kind === 'final' || e.kind === 'other')) {
    return e.code === 'conversion_busy' ? 'busy' : (e.kind as 'busy' | 'network' | 'final' | 'other');
  }
  if (e?.status === 429) return 'busy';
  if (e?.status === 413 || e?.status === 415) return 'final';
  if (e?.name === 'TypeError' || e?.name === 'AbortError') return 'network';
  return 'other';
}

export class CollageIntake {
  private readonly env: CollageIntakeEnv;
  private readonly all: CollageJob[] = [];
  private readonly batches: Batch[] = [];
  private readonly queue: CollageJob[] = [];
  private readonly sourceMap = new Map<string, string>();
  private running = 0;
  private limit: number;
  private waitingOffline = 0;
  private busySince: number | undefined;
  private busyTimer: ReturnType<typeof setTimeout> | undefined;
  private batchCount = 0;
  /** Picks so far, one number per accepted file: the pool keeps pick order (§3). */
  private pickCount = 0;
  /** Asset id → its file's place in pick order, for the photos this intake sent. */
  private readonly pickRank = new Map<string, number>();
  /** Handed over or thrown away (another template, a rebuilt form): nothing more is sent or written. */
  private disposed = false;

  constructor(private readonly options: CollageIntakeOptions) {
    this.env = { ...defaultEnv(), ...(options.env ?? {}) };
    this.limit = this.initialLimit();
  }

  private initialLimit(): number {
    // Match the shared converter's two active jobs; a third would resend
    // the entire original after a conversion_busy response.
    return this.env.slow() ? 1 : 2;
  }

  // ── What the step reads ────────────────────────────────────────────────

  get jobs(): readonly CollageJob[] {
    return this.all;
  }

  /** Photos still queued, sending or waiting to retry: "Dalje" and the cart wait. */
  get sending(): boolean {
    return this.all.some((job) => job.state === 'waiting' || job.state === 'sending' || job.state === 'retrying');
  }

  /** Failed photos "Pošalji ponovo" can send again. */
  get retryable(): CollageJob[] {
    return this.all.filter((job) => job.state === 'failed' && job.retryable);
  }

  /** Failed photos that cannot be used at all (413/415, too big for the phone). */
  get unusable(): CollageJob[] {
    return this.all.filter((job) => job.state === 'failed' && !job.retryable);
  }

  /** How many uploads may run at once right now (2, 1 on a slow link; one less after each busy answer). */
  get concurrency(): number {
    return this.limit;
  }

  get offline(): boolean {
    return this.waitingOffline > 0;
  }

  /** The server has been asking us to wait for a while now. */
  get serverBusy(): boolean {
    return this.busySince !== undefined && this.env.now() - this.busySince >= BUSY_NOTICE_MS;
  }

  /** A batch put back after a reload is still on its way. */
  get resuming(): boolean {
    return this.batches.some((b) => b.resumed && !b.settled && b.jobs.some((j) => j.state !== 'done' && j.state !== 'failed'));
  }

  /** Done / total of the batches still on their way, by photos and by bytes. */
  progress(): CollageProgress | undefined {
    const live = this.batches.filter((b) => b.jobs.some((j) => j.state === 'waiting' || j.state === 'sending' || j.state === 'retrying'));
    if (live.length === 0) return undefined;
    const jobs = live.flatMap((b) => b.jobs);
    const done = jobs.filter((j) => j.state === 'done');
    return {
      done: done.length,
      total: jobs.length,
      bytesDone: done.reduce((sum, j) => sum + j.bytes, 0),
      bytesTotal: jobs.reduce((sum, j) => sum + j.bytes, 0),
    };
  }

  /** Asset id → the fingerprint of the file it came from (kept in the draft). */
  sources(): Record<string, string> {
    return Object.fromEntries(this.sourceMap);
  }

  /** Fingerprints from a draft, so a resumed or re-picked photo is not sent twice. */
  restoreSources(map: Record<string, string> | undefined): void {
    for (const [assetId, fingerprint] of Object.entries(map ?? {})) {
      if (typeof fingerprint === 'string' && fingerprint) this.sourceMap.set(assetId, fingerprint);
    }
  }

  /**
   * What the product takes: `max` = active slots + spares, `left` = that
   * less the photos in the answer and those still on their way (a failed one
   * "Pošalji ponovo" can send keeps its place). A replacement ("Nova slika")
   * takes the place of the photo it replaces and counts for nothing.
   */
  capacity(): { max: number; left: number } {
    const host = this.options.host;
    const rules = host.rules;
    const active = Math.min(host.activeSlots().length, rules.targetElementIds.length || Infinity);
    const max = active + rules.maxSpare;
    const inflight = this.all.filter(
      (j) => !j.target && (j.state === 'waiting' || j.state === 'sending' || j.state === 'retrying' || (j.state === 'failed' && j.retryable)),
    ).length;
    return { max, left: Math.max(0, max - (host.answer()?.photos.length ?? 0) - inflight) };
  }

  // ── Picking ────────────────────────────────────────────────────────────

  /**
   * Takes a selection in. `resumed` files come back from IndexedDB after a
   * reload, under their old `indices`; a `target` is the one slot a single
   * photo replaces ("Nova slika").
   */
  async add(
    files: readonly File[],
    opts: { resumed?: boolean; indices?: readonly (string | undefined)[]; target?: string } = {},
  ): Promise<CollageAddOutcome> {
    const host = this.options.host;
    const rules = host.rules;
    const resumed = opts.resumed === true;
    if (this.disposed) return { accepted: 0, duplicates: 0, refused: files.length };
    const answer = host.answer();
    const inAnswer = new Set((answer?.photos ?? []).map((p) => p.assetId));
    const known: string[] = [];
    for (const [assetId, fingerprint] of this.sourceMap) if (inAnswer.has(assetId)) known.push(fingerprint);
    for (const job of this.all) {
      if (job.state === 'done' || (job.state === 'failed' && !job.retryable)) continue;
      known.push(job.fingerprint);
    }

    const batchId = `${Math.round(this.env.now()).toString(36)}${(this.batchCount++).toString(36)}`;
    const picked: Array<{ file: File; fingerprint: string; index?: string }> = [];
    let duplicates = 0;
    // "Nova slika" with a photo that is already in: it simply goes there
    // too (a repeat, if the question allows one) — nothing is sent again.
    if (opts.target && answer && files.length > 0) {
      const fingerprint = collageFingerprint(files[0]!);
      const existing = [...this.sourceMap].find(([assetId, known]) => known === fingerprint && inAnswer.has(assetId))?.[0];
      const photo = existing ? answer.photos.find((p) => p.assetId === existing) : undefined;
      if (photo) {
        const next = replaceInSlot(answer, opts.target, photo, this.context());
        if (next !== answer) host.commit(next);
        this.update();
        return { accepted: 0, duplicates: 1, refused: 0 };
      }
    }
    files.forEach((file, i) => {
      const fingerprint = collageFingerprint(file);
      const index = opts.indices?.[i];
      if (known.some((k) => sameSource(k, fingerprint, resumed)) || picked.some((p) => p.fingerprint === fingerprint)) {
        duplicates++;
        if (resumed && index) void this.env.dropPending(this.options.scope, this.options.optionId, index).catch(() => undefined);
        return;
      }
      picked.push({ file, fingerprint, ...(index ? { index } : {}) });
    });

    // Capacity: what the product holds (active slots + spares) less what is
    // in or on its way. A replacement takes the place of the photo it replaces.
    let accepted = picked;
    let message: string | undefined;
    if (!opts.target) {
      const { max, left: cap } = this.capacity();
      if (picked.length > cap) {
        accepted = picked.slice(0, cap);
        message = cap === 0 ? COLLAGE_TEXTS.full : COLLAGE_TEXTS.capped(max, cap);
        if (resumed) {
          for (const left of picked.slice(cap)) {
            if (left.index) void this.env.dropPending(this.options.scope, this.options.optionId, left.index).catch(() => undefined);
          }
        }
      }
    } else {
      accepted = picked.slice(0, 1);
    }
    const refused = picked.length - accepted.length;
    if (accepted.length === 0) {
      this.update();
      return { accepted: 0, duplicates, refused, ...(message ? { message } : {}) };
    }

    // A new pick starts clean: the unusable ones of the last pick were said.
    if (!resumed) {
      for (let i = this.all.length - 1; i >= 0; i--) {
        const job = this.all[i]!;
        if (job.state === 'failed' && !job.retryable) this.all.splice(i, 1);
      }
    }

    // Their places in pick order, taken before anything is awaited: a second
    // pick made meanwhile comes after this one, whichever headers read first.
    const firstOrder = this.pickCount;
    this.pickCount += accepted.length;
    // Sizes from the headers only — 64 KB per file, nothing decoded.
    const sizes = await Promise.all(accepted.map((p) => this.env.pictureSize(p.file).catch(() => undefined)));
    if (this.disposed) return { accepted: 0, duplicates, refused: picked.length };
    const jobs: CollageJob[] = accepted.map((p, i) => ({
      key: p.index ?? `${batchId}.${i}`,
      batch: batchId,
      file: p.file,
      name: p.file.name || 'slika',
      fingerprint: p.fingerprint,
      bytes: Math.max(1, p.file.size || 0),
      ...(sizes[i] ? { size: sizes[i] } : {}),
      plannedArea: 0,
      ...(opts.target ? { target: opts.target, plannedSlot: opts.target } : {}),
      order: firstOrder + i,
      state: 'waiting' as const,
      retryable: true,
      resumed,
      busyTries: 0,
      networkTries: 0,
      kept: Promise.resolve(),
    }));

    // The provisional arrangement, from the headers: the empty active slots
    // no photo on its way has claimed, by the same placement as the answer.
    const current = host.answer();
    const seed = current?.seed ?? randomCollageSeed(() => this.env.random());
    if (!opts.target) {
      const reserved = this.reserved();
      const empties = host.activeSlots().filter((s) => !current?.slots[s.elementId] && !reserved.has(s.elementId));
      const plan = planCollagePlacement(
        jobs.map((job) => ({ assetId: job.key, ...(job.size ? { w: job.size.w, h: job.size.h } : {}) })),
        empties,
        { seed, faceFraming: rules.faceFraming },
      );
      const area = new Map(empties.map((s) => [s.elementId, s.area] as const));
      for (const [elementId, key] of Object.entries(plan.bySlot)) {
        const job = jobs.find((j) => j.key === key);
        if (!job) continue;
        job.plannedSlot = elementId;
        job.plannedArea = area.get(elementId) ?? 0;
      }
    }
    // Biggest planned slot first, spares last; pick order breaks ties.
    const ordered = jobs
      .map((job, i) => ({ job, i }))
      .sort((a, b) => b.job.plannedArea - a.job.plannedArea || a.i - b.i)
      .map((x) => x.job);

    // Kept one after another, in sending order; each job waits for its own.
    let chain: Promise<void> = Promise.resolve();
    for (const job of ordered) {
      if (job.resumed) continue;
      chain = chain.then(() =>
        this.env.keepPending(this.options.scope, this.options.optionId, job.file, job.key).catch(() => undefined),
      );
      job.kept = chain;
    }

    if (!this.sending) this.limit = this.initialLimit();
    this.batches.push({ id: batchId, seed, touchesAtStart: host.touches(), jobs: ordered, resumed, settled: false });
    this.all.push(...ordered);
    this.queue.push(...ordered);
    this.update();
    this.pump();
    return { accepted: accepted.length, duplicates, refused, ...(message ? { message } : {}) };
  }

  /** Pictures IndexedDB kept through a reload: one batch, under their old indices. */
  resume(pending: readonly PendingPicture[]): Promise<CollageAddOutcome> {
    return this.add(pending.map((p) => p.file), { resumed: true, indices: pending.map((p) => p.index) });
  }

  /** "Pošalji ponovo": every failed photo that sending again can help, from memory. */
  retryFailed(): void {
    for (const job of this.retryable) this.requeue(job);
    this.pump();
  }

  /** One "!" thumbnail tapped. */
  retry(key: string): void {
    const job = this.all.find((j) => j.key === key);
    if (!job || job.state !== 'failed' || !job.retryable) return;
    this.requeue(job);
    this.pump();
  }

  private requeue(job: CollageJob): void {
    job.state = 'waiting';
    job.busyTries = 0;
    job.networkTries = 0;
    delete job.message;
    const batch = this.batches.find((b) => b.id === job.batch);
    if (batch) batch.settled = false;
    this.queue.push(job);
    this.update();
  }

  /** Slots a photo still on its way was planned for. */
  private reserved(except?: CollageJob): Set<string> {
    const out = new Set<string>();
    for (const job of this.all) {
      if (job === except || job.target || !job.plannedSlot) continue;
      if (job.state === 'waiting' || job.state === 'sending' || job.state === 'retrying') out.add(job.plannedSlot);
    }
    return out;
  }

  // ── Sending ────────────────────────────────────────────────────────────

  private pump(): void {
    if (this.disposed) return;
    while (this.running < this.limit && this.queue.length > 0) {
      const job = this.queue.shift()!;
      if (job.state !== 'waiting') continue;
      this.running++;
      void this.run(job).finally(() => {
        this.running--;
        this.afterJob(job);
        this.pump();
      });
    }
  }

  private async run(job: CollageJob): Promise<void> {
    job.state = 'sending';
    this.update();
    await job.kept;
    if (this.disposed) return;
    let ready: File;
    try {
      ready = await this.env.fitForUpload(job.file, job.size);
    } catch (err) {
      this.fail(job, 'final', err);
      return;
    }
    for (;;) {
      // Handed over meanwhile: the new form's intake sends it (see `handOver`).
      if (this.disposed) return;
      try {
        const result = await this.options.upload(ready, ready.name || job.name);
        this.landed(job, result);
        return;
      } catch (err) {
        if (this.disposed) return;
        const kind = classifyUploadError(err);
        if (kind === 'busy' && job.busyTries < COLLAGE_BUSY_TRIES) {
          job.busyTries++;
          // One upload fewer at a time for the rest of the batch.
          this.limit = Math.max(1, this.limit - 1);
          job.state = 'retrying';
          this.noteBusy();
          this.update();
          await this.env.sleep(BUSY_PAUSE[0] + this.env.random() * (BUSY_PAUSE[1] - BUSY_PAUSE[0]));
          job.state = 'sending';
          continue;
        }
        if (kind === 'network' && job.networkTries < COLLAGE_NETWORK_PAUSES.length) {
          const pause = COLLAGE_NETWORK_PAUSES[job.networkTries]!;
          job.networkTries++;
          job.state = 'retrying';
          this.update();
          await this.env.sleep(pause);
          await this.whileOffline();
          job.state = 'sending';
          continue;
        }
        this.fail(job, kind, err);
        return;
      }
    }
  }

  private async whileOffline(): Promise<void> {
    if (this.env.online()) return;
    this.waitingOffline++;
    this.update();
    try {
      while (!this.env.online()) await this.env.whenOnline();
    } finally {
      this.waitingOffline--;
      this.update();
    }
  }

  private noteBusy(): void {
    if (this.busySince !== undefined) return;
    this.busySince = this.env.now();
    clearTimeout(this.busyTimer);
    // The notice appears after a while without anything else happening.
    this.busyTimer = setTimeout(() => this.update(), BUSY_NOTICE_MS + 50);
  }

  private fail(job: CollageJob, kind: 'busy' | 'network' | 'final' | 'other', err: unknown): void {
    if (this.disposed) return;
    job.state = 'failed';
    job.retryable = kind !== 'final';
    // A file the server (or the phone) cannot use for good is not kept for a
    // reload either: sending the same bytes again only earns the same refusal.
    // A photo that may still go through stays kept.
    if (!job.retryable) void this.env.dropPending(this.options.scope, this.options.optionId, job.key).catch(() => undefined);
    const reason = err instanceof Error && err.message ? err.message : '';
    // A lost connection or a busy server says nothing about the file: the
    // plain "not sent" (the tray's "!" and "Pošalji ponovo" do the rest).
    job.message =
      kind === 'final'
        ? [COLLAGE_TEXTS.unusable(job.name), reason].filter(Boolean).join(' ')
        : kind === 'other' && reason
          ? reason
          : COLLAGE_TEXTS.notSent(1);
    this.update();
  }

  private landed(job: CollageJob, result: CollageUploadResult): void {
    // Handed over while it was on its way: the new form sends it itself.
    if (this.disposed) return;
    const host = this.options.host;
    const block = result.collage;
    const whole = (n: unknown): number | undefined =>
      typeof n === 'number' && Number.isFinite(n) && n >= 1 ? Math.round(n) : undefined;
    const w = whole(block?.width) ?? whole(result.width) ?? job.size?.w;
    const h = whole(block?.height) ?? whole(result.height) ?? job.size?.h;
    const photo: CollagePhoto = {
      assetId: result.id,
      ...(w && h ? { w, h } : {}),
      ...(Array.isArray(block?.faces) ? { faces: block!.faces } : {}),
    };
    const drawUrl = block?.drawUrl ?? result.previewUrl ?? result.url;
    const media: CollageMedia = {
      assetId: result.id,
      drawUrl,
      thumbUrl: block?.thumbUrl ?? drawUrl,
      framerUrl: result.previewUrl ?? result.url,
      ...(w && h ? { size: { w, h } } : {}),
      fingerprint: job.fingerprint,
    };
    // The pixels first: the answer that points at them redraws the preview.
    host.media(media);
    this.sourceMap.set(result.id, job.fingerprint);
    this.pickRank.set(result.id, job.order);
    const batch = this.batches.find((b) => b.id === job.batch);
    const seed = batch?.seed ?? randomCollageSeed(() => this.env.random());

    // Done BEFORE the answer is written: storing it tells the page at once
    // (the form's onChange: the required meter, "Dalje", Studio's state), and
    // what the page reads then must already say this photo is no longer on
    // its way — for the last one of a batch, that nothing is.
    job.state = 'done';
    job.assetId = result.id;
    if (job.target) {
      const current = host.answer() ?? emptyCollageAnswer(seed);
      const next = replaceInSlot(current, job.target, photo, this.context());
      if (next !== current) host.commit(this.inPickOrder(next, photo.assetId));
    } else {
      const reserved = this.reserved(job);
      host.land((answer) => {
        const next = this.placeLanded(answer, photo, job, reserved, seed);
        return next && next !== answer ? this.inPickOrder(next, photo.assetId) : next;
      });
    }

    const kept = host.answer()?.photos.some((p) => p.assetId === photo.assetId) === true;
    if (!kept) {
      // No room after all (the shopper switched to a smaller layout meanwhile).
      job.state = 'failed';
      job.retryable = false;
      job.message = COLLAGE_TEXTS.full;
      delete job.assetId;
    }
    this.update();
    this.options.flushDraft?.();
    void this.env.dropPending(this.options.scope, this.options.optionId, job.key).catch(() => undefined);
  }

  /**
   * Where a landed photo goes in `answer` (the current one, or an undo
   * snapshot): its planned slot while that is empty and active; otherwise the
   * placement's choice among the empty slots no other photo on its way has
   * claimed, or "Rezerva".
   */
  private placeLanded(
    answer: CollageAnswer | null,
    photo: CollagePhoto,
    job: CollageJob,
    reserved: Set<string>,
    seed: number,
  ): CollageAnswer | null {
    const base = answer ?? emptyCollageAnswer(seed);
    if (base.photos.some((p) => p.assetId === photo.assetId)) return base;
    const ctx = this.context();
    const planned = job.plannedSlot;
    if (planned && !base.slots[planned] && ctx.slots.some((s) => s.elementId === planned)) {
      const next = replaceInSlot(base, planned, photo, ctx);
      if (next !== base) return next;
    }
    const free = ctx.slots.filter((s) => base.slots[s.elementId] || !reserved.has(s.elementId));
    const added = addCollagePhotos(base, [photo], { slots: free, rules: ctx.rules });
    return added.answer.photos.some((p) => p.assetId === photo.assetId) ? added.answer : answer;
  }

  /**
   * The pool in pick order (docs/photo-collage.md §3), whatever order the
   * uploads land in (biggest slot first, two at a time): the photo that just
   * landed moves in front of the first photo picked after it. Photos this
   * intake did not send (a restored order, a draft) count as picked before.
   * Only the pool's order changes — which photo is in which slot does not.
   */
  private inPickOrder(answer: CollageAnswer, assetId: string): CollageAnswer {
    const rank = this.pickRank.get(assetId);
    const at = answer.photos.findIndex((p) => p.assetId === assetId);
    if (rank === undefined || at < 0) return answer;
    const rest = answer.photos.filter((_, i) => i !== at);
    let to = rest.findIndex((p) => (this.pickRank.get(p.assetId) ?? -1) > rank);
    if (to < 0) to = rest.length;
    if (to === at) return answer;
    return { ...answer, photos: [...rest.slice(0, to), answer.photos[at]!, ...rest.slice(to)] };
  }

  /**
   * The form is being rebuilt for another template ("Change Template"): the
   * photos still on their way — queued, sending, waiting to retry, or failed
   * but worth sending again — are handed to the new form's intake, which
   * sends them there. This one stops: nothing more is sent, and a photo in
   * flight that lands anyway is not written into the old form's answer. The
   * files kept for a reload under this form's scope are forgotten (the new
   * intake keeps them under its own).
   */
  handOver(): File[] {
    const files: File[] = [];
    for (const job of this.all) {
      const live = job.state === 'waiting' || job.state === 'sending' || job.state === 'retrying' || (job.state === 'failed' && job.retryable);
      // A "Nova slika" goes over as a plain photo: its slot is the old template's.
      if (!live) continue;
      files.push(job.file);
      void this.env.dropPending(this.options.scope, this.options.optionId, job.key).catch(() => undefined);
    }
    // Pick order, not sending order.
    const order = new Map(this.all.map((job) => [job.file, job.order] as const));
    files.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
    this.dispose();
    return files;
  }

  /** The step is gone (another product, a rebuilt form): nothing more is sent, no more notices. */
  dispose(): void {
    this.disposed = true;
    this.queue.length = 0;
    clearTimeout(this.busyTimer);
    this.busyTimer = undefined;
  }

  /** Something the step shows changed — said only while this intake is the form's (never after `handOver`). */
  private update(): void {
    if (!this.disposed) this.options.onUpdate?.();
  }

  private context(): CollageContext & { slots: CollageSlotGeom[] } {
    return { slots: this.options.host.activeSlots(), rules: this.options.host.rules };
  }

  // ── After a batch ──────────────────────────────────────────────────────

  private afterJob(job: CollageJob): void {
    if (this.disposed) return;
    const batch = this.batches.find((b) => b.id === job.batch);
    if (!batch || batch.settled) return;
    if (batch.jobs.some((j) => j.state === 'waiting' || j.state === 'sending' || j.state === 'retrying')) return;
    batch.settled = true;
    if (!this.sending) {
      this.busySince = undefined;
      clearTimeout(this.busyTimer);
    }
    try {
      this.finalPass(batch);
    } catch {
      /* the provisional arrangement stays; it is a good one */
    }
    // Landed photos live in the answer now; the tray shows them from there.
    for (const done of batch.jobs.filter((j) => j.state === 'done')) {
      const at = this.all.indexOf(done);
      if (at >= 0) this.all.splice(at, 1);
    }
    batch.jobs = batch.jobs.filter((j) => j.state !== 'done');
    this.update();
  }

  /**
   * The provisional plan knew the photos' sizes; now their faces are known
   * too. With the same seed (so the chance term is the same), the batch's own
   * photos are placed again over the slots they hold and the empty ones —
   * applied only when fewer faces end up cut, and only when the shopper has
   * not touched anything since the batch began.
   */
  private finalPass(batch: Batch): void {
    const host = this.options.host;
    if (host.touches() !== batch.touchesAtStart) return;
    const answer = host.answer();
    if (!answer) return;
    const ctx = this.context();
    const rules = ctx.rules;
    if (!rules.faceFraming) return;
    const before = collageStatus(answer, ctx.slots, rules);
    if (before.repeats > 0 || before.facesCut.length === 0) return;
    const keyOf = new Map<string, string>();
    for (const job of batch.jobs) if (job.state === 'done' && job.assetId) keyOf.set(job.assetId, job.key);
    if (keyOf.size < 2) return;
    const assetOf = new Map([...keyOf].map(([asset, key]) => [key, asset] as const));
    const reservedByOthers = this.reserved();
    const subset = ctx.slots.filter((s) => {
      const fill = answer.slots[s.elementId];
      if (!fill) return !reservedByOthers.has(s.elementId);
      return keyOf.has(fill.assetId) && !fill.manual;
    });
    const activeIds = ctx.slots.map((s) => s.elementId);
    const spares = new Set(collageSpares(answer, activeIds));
    const inSubset = new Set(subset.map((s) => answer.slots[s.elementId]?.assetId).filter(Boolean) as string[]);
    const photos = answer.photos
      .filter((p) => keyOf.has(p.assetId) && (inSubset.has(p.assetId) || spares.has(p.assetId)))
      .map((p) => ({ ...p, assetId: keyOf.get(p.assetId)! }));
    if (!photos.some((p) => p.faces && p.faces.length > 0)) return;
    const plan = planCollagePlacement(photos, subset, { seed: batch.seed, faceFraming: true });
    const want = new Map<string, string>();
    for (const [elementId, key] of Object.entries(plan.bySlot)) {
      const assetId = assetOf.get(key);
      if (assetId) want.set(elementId, assetId);
    }
    let next = answer;
    const done = new Set<string>();
    for (const slot of subset) {
      const id = slot.elementId;
      const wanted = want.get(id);
      done.add(id);
      if (!wanted || next.slots[id]?.assetId === wanted) continue;
      const holder = subset.find((s) => !done.has(s.elementId) && next.slots[s.elementId]?.assetId === wanted);
      if (holder) next = exchangeCollage(next, { slot: id }, { slot: holder.elementId }, ctx);
      else if (collageSpares(next, activeIds).includes(wanted)) next = exchangeCollage(next, { slot: id }, { spare: wanted }, ctx);
    }
    if (next === answer) return;
    const after = collageStatus(next, ctx.slots, rules);
    if (after.facesCut.length < before.facesCut.length && after.filled === before.filled) host.settle(answer, next);
  }
}
